"use client"

import { motion, AnimatePresence } from "framer-motion"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { MapPin, Phone, Mail, Check, ChevronDown, Star, Shield, Clock, Users, Truck, Home, Building2, Package, ArrowRight, CheckCircle2, Award, Zap } from "lucide-react"
import Link from "next/link"
import { useState } from "react"
import type { CityData } from "@/lib/cities-data"

export function LocationDetail({ city }: { city: CityData }) {
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null)

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { 
        staggerChildren: 0.12,
        delayChildren: 0.1 
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { duration: 0.6, ease: "easeOut" as const } 
    },
  }

  const statsVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" as const }
    }
  }

  const serviceIcons = {
    "Home Relocation": Home,
    "Office Moving": Building2,
    "Packing Services": Package,
    "Local Moving": Truck,
    "Long Distance": MapPin,
    "Storage": Package
  }

  return (
    <div className="min-h-screen">
      {/* Enhanced Hero Section */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        {/* Modern gradient background matching brand colors */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1e40af] via-[#2563eb] to-[#3b82f6]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(37,99,235,0.3),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_70%,rgba(249,115,22,0.2),transparent_50%)]" />
        
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-blue-500/10 to-blue-400/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-r from-orange-500/10 to-orange-400/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Trust Badge */}
            <motion.div
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-orange-500/20 to-orange-600/20 border border-orange-400/30 text-orange-200 font-medium text-sm mb-8"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <CheckCircle2 size={16} />
              Trusted by 1000+ Customers in {city.name}
            </motion.div>

            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-8">
              <span className="bg-gradient-to-r from-white via-blue-100 to-orange-100 bg-clip-text text-transparent">
                Best Packers & Movers in
              </span>
              <br />
              <span className="bg-gradient-to-r from-orange-400 via-orange-500 to-blue-400 bg-clip-text text-transparent">
                {city.name}
              </span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-blue-100 max-w-4xl mx-auto leading-relaxed mb-12">
              Professional relocation services in {city.name}, {city.state}. 
              ISO certified, 100% insured, and trusted by thousands of satisfied customers.
            </p>

            {/* Quick Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <motion.div
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button 
                  size="lg" 
                  className="px-8 py-4 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold text-lg shadow-2xl border-0 rounded-full"
                >
                  <Zap size={20} className="mr-2" />
                  Get Free Quote in {city.name}
                  <ArrowRight size={20} className="ml-2" />
                </Button>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  size="lg"
                  variant="outline"
                  className="px-6 py-4 border-2 border-white/30 text-white hover:bg-white/10 font-semibold bg-white/5 backdrop-blur-sm rounded-full"
                >
                  <Phone size={20} className="mr-2" />
                  Call: {city.contactInfo.phone}
                </Button>
              </motion.div>
            </div>

            {/* Key Stats for the city */}
            <motion.div
              className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto justify-center"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {city.localStats.map((stat, index) => (
                <motion.div
                  key={index}
                  variants={statsVariants}
                  className="text-center group flex flex-col items-center"
                >
                  <div className="w-16 h-16 mb-4 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Star size={24} className="text-orange-300" />
                  </div>
                  <div className="text-3xl md:text-4xl font-bold text-white mb-2">{stat.value}</div>
                  <div className="text-blue-200 text-sm font-medium">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us in City */}
      <section className="py-10 sm:py-20 bg-gradient-to-b from-white to-blue-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-100 text-orange-700 font-medium text-sm mb-6">
              <Award size={16} />
              Why Choose Bigwin in {city.name}
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              {city.name}'s Most Trusted Moving Partner
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Experience hassle-free relocation with our professional team in {city.name}. 
              We understand local requirements and provide personalized moving solutions.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {[
              { icon: Shield, title: "100% Insured", desc: "Complete protection for your belongings", color: "from-blue-500 to-blue-600" },
              { icon: Clock, title: "On-Time Delivery", desc: "Punctual service guaranteed", color: "from-orange-500 to-orange-600" },
              { icon: Users, title: "Expert Team", desc: "Trained professionals in {city.name}", color: "from-blue-500 to-blue-600" },
              { icon: Award, title: "5-Star Rated", desc: "Highest customer satisfaction", color: "from-orange-500 to-orange-600" }
            ].map((feature, index) => {
              const Icon = feature.icon
              return (
                <motion.div
                  key={index}
                  className="text-center group"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r ${feature.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <Icon size={28} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.desc.replace('{city.name}', city.name)}</p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-20 sm:pb-28 bg-gradient-to-b from-blue-50/30 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <motion.div 
              className="lg:col-span-2 space-y-12"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {/* Services Section */}
              <motion.div variants={itemVariants}>
                <Card className="p-8 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-orange-500 flex items-center justify-center">
                      <Truck size={24} className="text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900">Our Services in {city.name}</h2>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {city.services.map((service, index) => {
                      const IconComponent = serviceIcons[service as keyof typeof serviceIcons] || Package
                      return (
                        <motion.div
                          key={index}
                          className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-blue-50 to-orange-50 border border-blue-100 hover:shadow-md transition-all duration-300"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ scale: 1.02 }}
                        >
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-blue-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                            <IconComponent size={20} className="text-white" />
                          </div>
                          <span className="text-gray-900 font-semibold">{service}</span>
                        </motion.div>
                      )
                    })}
                  </div>
                </Card>
              </motion.div>

              {/* Coverage Area */}
              <motion.div variants={itemVariants}>
                <Card className="p-8 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-orange-500 to-blue-500 flex items-center justify-center">
                      <MapPin size={24} className="text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900">Service Coverage Areas</h2>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {city.coverageArea.map((area, index) => (
                      <motion.div
                        key={index}
                        className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-green-50 to-blue-50 border border-green-100"
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05 }}
                        whileHover={{ scale: 1.05 }}
                      >
                        <CheckCircle2 size={18} className="text-green-600 flex-shrink-0" />
                        <span className="text-gray-900 font-medium text-sm">{area}</span>
                      </motion.div>
                    ))}
                  </div>
                </Card>
              </motion.div>

              {/* FAQ Section */}
              <motion.div variants={itemVariants}>
                <Card className="p-8 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-orange-500 flex items-center justify-center">
                      <CheckCircle2 size={24} className="text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900">Frequently Asked Questions</h2>
                  </div>
                  
                  <div className="space-y-4">
                    {city.faqs.map((item, index) => (
                      <motion.div
                        key={index}
                        className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <button
                          onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                          className="w-full p-6 text-left hover:bg-gray-50 transition-colors flex items-center justify-between gap-4"
                          aria-expanded={expandedFAQ === index}
                        >
                          <span className="font-semibold text-gray-900 text-lg">
                            {item.question}
                          </span>
                          <motion.div
                            animate={{ rotate: expandedFAQ === index ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                            className="flex-shrink-0"
                          >
                            <ChevronDown size={24} className="text-blue-600" />
                          </motion.div>
                        </button>
                        
                        <AnimatePresence>
                          {expandedFAQ === index && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                              className="border-t border-gray-100"
                            >
                              <div className="p-6 bg-gradient-to-r from-blue-50/30 to-orange-50/30">
                                <p className="text-gray-700 leading-relaxed">
                                  {item.answer}
                                </p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))}
                  </div>
                </Card>
              </motion.div>
            </motion.div>

            {/* Enhanced Sidebar */}
            <motion.div 
              className="lg:col-span-1 space-y-8"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {/* Contact Card */}
              <motion.div variants={itemVariants}>
                <Card className="p-8 bg-gradient-to-br from-orange-50 to-blue-50 border-0 shadow-lg">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r from-orange-500 to-blue-500 flex items-center justify-center">
                      <Phone size={28} className="text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Contact {city.name} Office</h3>
                    <p className="text-gray-600">Get instant quote & support</p>
                  </div>
                  
                  <div className="space-y-6">
                    <motion.div 
                      className="flex items-start gap-4 p-4 rounded-xl bg-white/80 backdrop-blur-sm"
                      whileHover={{ scale: 1.02 }}
                    >
                      <Phone size={20} className="text-orange-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Phone</p>
                        <a
                          href={`tel:${city.contactInfo.phone}`}
                          className="text-gray-900 font-bold text-lg hover:text-orange-600 transition-colors"
                        >
                          {city.contactInfo.phone}
                        </a>
                      </div>
                    </motion.div>
                    
                    <motion.div 
                      className="flex items-start gap-4 p-4 rounded-xl bg-white/80 backdrop-blur-sm"
                      whileHover={{ scale: 1.02 }}
                    >
                      <Mail size={20} className="text-blue-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Email</p>
                        <a
                          href={`mailto:${city.contactInfo.email}`}
                          className="text-gray-900 font-semibold hover:text-blue-600 transition-colors break-all"
                        >
                          {city.contactInfo.email}
                        </a>
                      </div>
                    </motion.div>
                    
                    <motion.div 
                      className="flex items-start gap-4 p-4 rounded-xl bg-white/80 backdrop-blur-sm"
                      whileHover={{ scale: 1.02 }}
                    >
                      <MapPin size={20} className="text-orange-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Address</p>
                        <p className="text-gray-900 font-semibold leading-relaxed">{city.contactInfo.address}</p>
                      </div>
                    </motion.div>
                  </div>
                </Card>
              </motion.div>

              {/* Enhanced CTA */}
              <motion.div variants={itemVariants}>
                <Card className="p-8 bg-gradient-to-br from-blue-600 to-orange-600 text-white border-0 shadow-xl">
                  <div className="text-center">
                    <Zap size={32} className="mx-auto mb-4 text-yellow-300" />
                    <h3 className="text-2xl font-bold mb-4">Ready to Move?</h3>
                    <p className="text-blue-100 mb-6">Get instant free quote for your {city.name} relocation</p>
                    
                    <Link href="/get-quote" className="block">
                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Button className="w-full bg-white text-blue-600 hover:bg-gray-100 font-bold py-4 text-lg rounded-xl shadow-lg">
                          Get Free Quote Now
                          <ArrowRight size={20} className="ml-2" />
                        </Button>
                      </motion.div>
                    </Link>
                    
                    <p className="text-xs text-blue-200 mt-4">
                      ✓ No hidden charges ✓ Instant response ✓ 100% free
                    </p>
                  </div>
                </Card>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}
